var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Disclosure } from "@headlessui/react";
import { useReactTable, getCoreRowModel } from "@tanstack/react-table";
import { useData, DateRangeSelector, LoadingIcon, TableBase, } from "ee-react-components";
import { GetDisplayPrice, NumberLocaleStringStyle } from "ee-utils";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
var ExpandableTableBase = function (props) {
    var expandableTitle = props.expandableTitle, _a = props.defaultOpen, defaultOpen = _a === void 0 ? false : _a, tableBaseProps = __rest(props, ["expandableTitle", "defaultOpen"]);
    return (React.createElement(Disclosure, { defaultOpen: defaultOpen }, function (_a) {
        var open = _a.open;
        return (React.createElement(React.Fragment, null,
            React.createElement(Disclosure.Button, { className: "bg-black-1000 hover:bg-black-800 rounded-[10px] px-[32px] py-[16px] flex flex-row gap-x-[32px] w-full justify-between" },
                React.createElement("label", { className: "font-N6" }, expandableTitle),
                open ? (React.createElement(ChevronDownIcon, { className: "w-[32px] h-[32px]" })) : (React.createElement(ChevronUpIcon, { className: "w-[32px] h-[32px]" }))),
            React.createElement(Disclosure.Panel, { className: "w-full" },
                React.createElement(TableBase, __assign({}, tableBaseProps)))));
    }));
};
var toEmspRows = function (breakdown) {
    return Object.entries(breakdown).map(function (_a) {
        var _b, _c;
        var key = _a[0], value = _a[1];
        return ({
            name: key === "standard" ? "Worldnet (Standard)" : (_b = value.emspName) !== null && _b !== void 0 ? _b : key,
            percentageReduction: (_c = value.emspPercentageRatioReduction) !== null && _c !== void 0 ? _c : 0,
            transactionAmount: value.totalTransactionAmount,
            transactionAmountPrecision: value.totalTransactionAmountPrecision,
            reconciledAmount: value.totalReconciledAmount,
            reconciledAmountPrecision: value.totalReconciledAmountPrecision,
        });
    });
};
var emspColumns = [
    {
        header: "Payment Type",
        accessorFn: function (row) { return row.name; },
    },
    {
        header: "Fee %",
        accessorFn: function (row) { var _a; return "".concat((((_a = row.percentageReduction) !== null && _a !== void 0 ? _a : 0) * 100).toFixed(1), "%"); },
    },
    {
        header: "Transaction Amount",
        accessorFn: function (row) {
            return "".concat(GetDisplayPrice(row.transactionAmount, row.transactionAmountPrecision, {
                maximumFractionDigits: 2,
                useLocaleString: true,
                localeStringStyle: NumberLocaleStringStyle.Currency,
            }));
        },
    },
    {
        header: "Reconciled Amount",
        accessorFn: function (row) {
            return "".concat(GetDisplayPrice(row.reconciledAmount, row.reconciledAmountPrecision, {
                maximumFractionDigits: 2,
                useLocaleString: true,
                localeStringStyle: NumberLocaleStringStyle.Currency,
            }));
        },
    },
];
var EmspBreakdownTable = React.memo(function (_a) {
    var emspBreakdown = _a.emspBreakdown;
    var rows = toEmspRows(emspBreakdown);
    var table = useReactTable({
        data: rows,
        columns: emspColumns,
        getCoreRowModel: getCoreRowModel(),
    });
    return (React.createElement(ExpandableTableBase, { expandableTitle: "Breakdown by Payment Provider", table: table, loading: false, columnDefinitions: emspColumns }));
});
var chargerColumns = [
    {
        header: "Charger Serial Number",
        accessorFn: function (row) { return row.chargerSerialNumber; },
    },
    {
        header: "kWh Dispensed",
        accessorFn: function (row) {
            return "".concat(row.totalKwhDispensed.toFixed(2), " kWh");
        },
    },
    {
        header: "kWh Transacted",
        accessorFn: function (row) {
            return "".concat(row.totalKwhAccountedFor.toFixed(2), " kWh");
        },
    },
    {
        header: "Transaction Amount",
        accessorFn: function (row) {
            return "".concat(GetDisplayPrice(row.totalTransactionAmount, row.totalTransactionAmountPrecision, {
                maximumFractionDigits: 2,
                useLocaleString: true,
                localeStringStyle: NumberLocaleStringStyle.Currency,
            }));
        },
    },
];
var ChargerTable = React.memo(function (_a) {
    var chargers = _a.chargers;
    var table = useReactTable({
        data: chargers,
        columns: chargerColumns,
        getCoreRowModel: getCoreRowModel(),
    });
    return (React.createElement(ExpandableTableBase, { expandableTitle: "Chargers", table: table, loading: false, columnDefinitions: chargerColumns }));
});
var SiteSection = React.memo(function (_a) {
    var site = _a.site;
    return (React.createElement(Disclosure, { defaultOpen: false }, function (_a) {
        var open = _a.open;
        return (React.createElement(React.Fragment, null,
            React.createElement(Disclosure.Button, { className: "bg-black-1000 hover:bg-black-700 rounded-[8px] px-[24px] py-[12px] flex flex-row w-full justify-between items-center" },
                React.createElement("div", { className: "flex flex-row gap-x-[8px] items-center" },
                    React.createElement("span", { className: "font-N6 text-white" }, site.siteName)),
                React.createElement("div", { className: "flex flex-row gap-x-[16px] items-center" },
                    React.createElement("span", { className: "text-gray-400 text-sm" },
                        site.totalKwhDispensed.toFixed(2),
                        " kWh dispensed"),
                    React.createElement("span", { className: "text-gray-400 text-sm" },
                        site.totalKwhAccountedFor.toFixed(2),
                        " kWh transacted"),
                    React.createElement("span", { className: "text-gray-400 text-sm" },
                        GetDisplayPrice(site.totalTransactionAmount, site.totalTransactionAmountPrecision, {
                            maximumFractionDigits: 2,
                            useLocaleString: true,
                            localeStringStyle: NumberLocaleStringStyle.Currency,
                        }),
                        " ",
                        "revenue"),
                    React.createElement("span", { className: "text-gray-400 text-sm" },
                        GetDisplayPrice(site.totalReconciliationAmount, site.totalReconciliationAmountPrecision, {
                            maximumFractionDigits: 2,
                            useLocaleString: true,
                            localeStringStyle: NumberLocaleStringStyle.Currency,
                        }),
                        " ",
                        "reconcile total"),
                    open ? (React.createElement(ChevronDownIcon, { className: "w-[24px] h-[24px]" })) : (React.createElement(ChevronUpIcon, { className: "w-[24px] h-[24px]" })))),
            React.createElement(Disclosure.Panel, { className: "w-full pl-[16px] flex flex-col gap-y-[8px] mt-[4px]" },
                React.createElement(EmspBreakdownTable, { emspBreakdown: site.emspBreakdown }),
                React.createElement(ChargerTable, { chargers: site.chargers }))));
    }));
});
var OrganizationSection = React.memo(function (_a) {
    var org = _a.org;
    return (React.createElement(Disclosure, { defaultOpen: false }, function (_a) {
        var open = _a.open;
        return (React.createElement(React.Fragment, null,
            React.createElement(Disclosure.Button, { className: "bg-black-1000 hover:bg-black-800 rounded-[10px] px-[32px] py-[16px] flex flex-row w-full justify-between items-center" },
                React.createElement("div", { className: "flex flex-row gap-x-[8px] items-center" },
                    React.createElement("span", { className: "font-N6 text-white" }, org.organizationName),
                    React.createElement("span", { className: "text-gray-400 text-sm" },
                        "(",
                        org.sites.length,
                        " ",
                        org.sites.length === 1 ? "site" : "sites",
                        ")")),
                React.createElement("div", { className: "flex flex-row gap-x-[16px] items-center" },
                    React.createElement("span", { className: "text-gray-400 text-sm" },
                        "Reconcile Total:",
                        GetDisplayPrice(org.totalReconciliationAmount, org.totalReconciliationAmountPrecision, {
                            maximumFractionDigits: 2,
                            useLocaleString: true,
                            localeStringStyle: NumberLocaleStringStyle.Currency,
                        })),
                    open ? (React.createElement(ChevronDownIcon, { className: "w-[32px] h-[32px]" })) : (React.createElement(ChevronUpIcon, { className: "w-[32px] h-[32px]" })))),
            React.createElement(Disclosure.Panel, { className: "w-full pl-[16px] flex flex-col gap-y-[8px] mt-[8px]" },
                React.createElement(EmspBreakdownTable, { emspBreakdown: org.emspBreakdown }),
                org.sites.map(function (site, i) { return (React.createElement(SiteSection, { key: i, site: site })); }))));
    }));
});
// --- Main component ---
export var NOCPaymentReconciliation = function (_a) {
    var earliestTransaction = _a.earliestTransaction;
    var _b = useAuth0(), isAuthenticated = _b.isAuthenticated, isLoading = _b.isLoading;
    var _c = useState([
        new Date(),
        new Date(),
    ]), dateRange = _c[0], setDateRange = _c[1];
    var earliestTransactionTime = new Date(earliestTransaction).getTime();
    var firstDate = useMemo(function () { return new Date(earliestTransactionTime); }, [earliestTransactionTime]);
    var buildSearchParams = useCallback(function () {
        var apiSearchParams = new URLSearchParams();
        var start = new Date(dateRange[0]);
        start.setHours(0, 0, 0, 0);
        var end = new Date(dateRange[1]);
        end.setHours(0, 0, 0, 0);
        apiSearchParams.append("start", start.toISOString());
        apiSearchParams.append("end", end.toISOString());
        return apiSearchParams;
    }, [dateRange]);
    var _d = useState(buildSearchParams), apiSearchParams = _d[0], setApiSearchParams = _d[1];
    var setNewParams = useCallback(function () {
        setApiSearchParams(buildSearchParams());
    }, [buildSearchParams]);
    var _e = useData("".concat(process.env.NOC_SVC_URL, "/charging-sessions/payment-reconciliation?").concat(apiSearchParams.toString()), !isLoading && isAuthenticated), paymentReconciliation = _e.data, dataIsLoading = _e.isLoading;
    return (React.createElement("div", { className: "flex flex-col gap-y-[16px]" },
        React.createElement("label", { className: "font-N5 text-white" }, "NOC Payment Reconciliation"),
        React.createElement(DateRangeSelector, { dateRange: dateRange, setDateRange: setDateRange, firstDate: firstDate, inclusiveRange: true }),
        React.createElement("button", { className: "px-[16px] py-[8px] rounded-[8px] bg-blue-700 hover:bg-blue-400 text-white w-fit", onClick: setNewParams }, "Get Reconciliation"),
        dataIsLoading ? (React.createElement("div", { className: "w-[32px] h-[32px]" }, LoadingIcon)) : (paymentReconciliation && (React.createElement("div", { className: "flex flex-col gap-y-[8px]" }, paymentReconciliation.map(function (org, i) { return (React.createElement(OrganizationSection, { key: i, org: org })); }))))));
};
